import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Instagram, Linkedin, Facebook } from "lucide-react";

export const SiteHeader = ({ firmName = "Zexo Legal" }) => {
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-border bg-white/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2.5 lg:px-10">
        <Link to="/" data-testid="header-logo" className="flex items-center">
          <img src="/logo-zexo.png" alt="Zexo Legal" className="h-24 w-24" width="96" height="96" />
        </Link>
        <nav className="hidden items-center gap-8 text-sm font-semibold text-slate-600 md:flex">
          <a href="/#how" data-testid="nav-how" className="transition-colors hover:text-navy">How it works</a>
          <Link to="/legal-notices" data-testid="nav-types" className="transition-colors hover:text-navy">Notice types</Link>
          <a href="/#faq" data-testid="nav-faq" className="transition-colors hover:text-navy">FAQ</a>
          <Link to="/track" data-testid="nav-track" className="transition-colors hover:text-navy">Track a notice</Link>
        </nav>
        <Link
          to="/create"
          data-testid="header-cta"
          className="rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-navy/30 transition-transform duration-200 hover:-translate-y-0.5 active:scale-95"
        >
          Start a notice
        </Link>
      </div>
    </header>
  );
};

export const SiteFooter = ({ firmName = "Zexo Legal" }) => (
  <footer data-testid="site-footer" className="border-t border-slate-800 bg-night text-slate-300">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-16 md:grid-cols-4 lg:px-10">
      <div className="md:col-span-2">
        <div className="flex items-center gap-2.5">
          <img src="/logo-zexo.png" alt="Zexo Legal" className="h-11 w-11" width="44" height="44" />
          <span className="font-display text-xl font-extrabold text-white">{firmName}</span>
        </div>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
          Legal Notices Made Easy.
        </p>
        <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
          <a href="https://www.instagram.com/zexolegal?igsi=enJhYmN4emRzeWVx" target="_blank" rel="noopener noreferrer"
            aria-label="Zexo Legal on Instagram" data-testid="social-instagram"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 text-slate-300 transition-colors hover:border-navy hover:bg-navy hover:text-white">
            <Instagram className="h-5 w-5" strokeWidth={2} />
          </a>
          <a href="https://www.linkedin.com/company/zexo-legal/" target="_blank" rel="noopener noreferrer"
            aria-label="Zexo Legal on LinkedIn" data-testid="social-linkedin"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 text-slate-300 transition-colors hover:border-navy hover:bg-navy hover:text-white">
            <Linkedin className="h-5 w-5" strokeWidth={2} />
          </a>
          <a href="https://www.facebook.com/share/18jd1CYiFu/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer"
            aria-label="Zexo Legal on Facebook" data-testid="social-facebook"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 text-slate-300 transition-colors hover:border-navy hover:bg-navy hover:text-white">
            <Facebook className="h-5 w-5" strokeWidth={2} />
          </a>
        </div>
      </div>
      <div>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-navy">Company</p>
        <ul className="space-y-2.5 text-sm text-slate-400">
          <li><a href="/#how" className="transition-colors hover:text-white">How it works</a></li>
          <li><Link to="/legal-notices" className="transition-colors hover:text-white">Notice types</Link></li>
          <li><a href="/#faq" className="transition-colors hover:text-white">FAQ</a></li>
          <li><Link to="/track" className="transition-colors hover:text-white">Track a notice</Link></li>
        </ul>
      </div>
      <div>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-navy">Contact</p>
        <ul className="space-y-3 text-sm text-slate-400">
          <li className="flex items-start gap-2.5">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-navy" strokeWidth={2} />
            <address data-testid="footer-address" className="not-italic leading-relaxed">
              833, Sector 15,<br />Gurugram - 122001
            </address>
          </li>
          <li className="flex items-center gap-2.5">
            <Mail className="h-4 w-4 shrink-0 text-navy" strokeWidth={2} />
            <a href="mailto:associate@zexolegal.com" data-testid="footer-email" className="transition-colors hover:text-white">
              associate@zexolegal.com
            </a>
          </li>
          <li className="flex items-center gap-2.5">
            <Phone className="h-4 w-4 shrink-0 text-navy" strokeWidth={2} />
            <a href="tel:+917906337384" data-testid="footer-phone" className="transition-colors hover:text-white">
              +91 7906337384
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
      © {new Date().getFullYear()} {firmName}. All rights reserved.
    </div>
  </footer>
);
